import { NSARouterMakerResult } from "namirasoft-account-react";
import { BaseMetaColumn, FilterItem, FilterItemOperator } from "namirasoft-core";
import { AccountRow, GifiReportRow, GifiStatementType, NamirasoftERPMetaDatabase } from "namirasoft-erp";
import { BaseColumnFormatter, FloatFormatter, IntegerFormatter, NSBoxBoolean, NSBoxDate, NSBoxEnum, NSButtonBlue, NSLine, NSLoading, NSPanel, NSRow, NSSpace, NSSpaceSizeType, NSTable, StringFormatter, StringFormatterSizeType, TableColumnInfo, TableRowInfo } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { ErpLayout } from "../ErpLayout";
import { ErpRouterProps } from "../ErpRouterProps";
import { ErpScope, ErpScopeField, ErpScopeMode } from "../ErpScope";

interface GifiReportPageProps extends NSARouterMakerResult, ErpRouterProps { }

interface GifiReportPageState
{
    loading: boolean;
    report: GifiReportRow | null;
    accountsById: Map<string, AccountRow>;
    scopeVersion: number;
}

interface MappedTableRow
{
    line_id: string;
    parent_line_id: string | null;
    gifi: string;
    gifi_name: string | null;
    statement_type: string | null;
    account_code: string | null;
    account_name: string | null;
    account_type: string | null;
    account_count: number | null;
    debit: string | number;
    credit: string | number;
    balance: string | number;
}

interface UnmappedTableRow
{
    account_id: string;
    parent_account_id: string | null;
    code: string;
    name: string;
    type: string;
    debit: string | number;
    credit: string | number;
    balance: string | number;
    is_group: boolean;
}

const CENTER_WRAP: React.CSSProperties = { width: "100%", display: "flex", justifyContent: "center" };
const CENTER_INNER: React.CSSProperties = { width: "100%", maxWidth: 1200, display: "flex", flexDirection: "column", gap: 16, minWidth: 0 };
const FULL_PANEL: React.CSSProperties = { width: "100%", maxWidth: "100%", boxSizing: "border-box" };
const TABLE_SCROLL: React.CSSProperties = { width: "100%", overflowX: "auto" };

// Account-type palette shared with the Account list / hierarchy page.
const TYPE_COLORS: { [k: string]: string } = {
    Asset: "#2e7d32",
    Liability: "#c62828",
    Equity: "#6a1b9a",
    Income: "#1565c0",
    Expense: "#ef6c00",
};

function renderTypeBadge(type: string | null | undefined): React.ReactNode
{
    if (!type) return "";
    let color = TYPE_COLORS[type] ?? "#555";
    return (
        <span style={{
            fontSize: 11,
            color,
            border: `1px solid ${color}`,
            borderRadius: 4,
            padding: "2px 8px",
            minWidth: 70,
            textAlign: "center",
            display: "inline-block",
        }}>
            {type}
        </span>
    );
}

export default class GifiReportPage extends Component<GifiReportPageProps, GifiReportPageState>
{
    private static readonly PARENT_PREFIX = "gifi:";
    private static readonly CHILD_PREFIX = "acc:";

    override state: GifiReportPageState = {
        loading: false,
        report: null,
        accountsById: new Map<string, AccountRow>(),
        scopeVersion: 0,
    };

    FromDate = createRef<NSBoxDate>();
    AsOf = createRef<NSBoxDate>();
    Statement = createRef<NSBoxEnum<GifiStatementType>>();
    ShowZero = createRef<NSBoxBoolean>();
    ConvertToBranchCurrency = createRef<NSBoxBoolean>();

    private unsubscribeScope: (() => void) | null = null;

    override componentDidMount(): void
    {
        this.unsubscribeScope = ErpScope.subscribe(() =>
        {
            this.setState(s => ({ scopeVersion: s.scopeVersion + 1, report: null, accountsById: new Map() }));
        });
    }

    override componentWillUnmount(): void { this.unsubscribeScope?.(); }

    private runReport = (onFinished: () => void): void =>
    {
        let companyId: string | null;
        let branchId: string | null;
        let fiscalYearId: string | null;
        try
        {
            companyId = ErpScope.getCompanyIdOrNull();
            branchId = ErpScope.getBranchIdOrNull();
            fiscalYearId = ErpScope.getFiscalYearIdOrNull();
        }
        catch (err)
        {
            this.props.notifier.onError(err as Error);
            onFinished();
            return;
        }

        let fromRaw = this.FromDate.current?.getValue(false) ?? null;
        let asOfRaw = this.AsOf.current?.getValue() ?? null;
        if (!asOfRaw) { onFinished(); return; }

        let statement = this.Statement.current?.getValueEnumOne(GifiStatementType, null as any) ?? null;
        let showZero = this.ShowZero.current?.getValueNull(false) ?? false;
        let convertToBranchCurrency = this.ConvertToBranchCurrency.current?.getValueNull(false) ?? false;

        this.setState({ loading: true });
        let reportPromise = this.props.servers.erp.report.Gifi(
            companyId,
            branchId,
            fiscalYearId,
            fromRaw ? new Date(fromRaw) : null,
            new Date(asOfRaw),
            statement,
            showZero,
            convertToBranchCurrency,
        );
        let accountsPromise = this.fetchAllAccounts(companyId, branchId);

        Promise.all([reportPromise, accountsPromise]).then(([res, accounts]: [GifiReportRow, AccountRow[]]) =>
        {
            let accountsById = new Map<string, AccountRow>();
            for (let a of accounts) accountsById.set(a.id, a);
            this.setState({ loading: false, report: res, accountsById });
            onFinished();
        }).catch(() =>
        {
            this.setState({ loading: false });
            onFinished();
        });
    };

    private async fetchAllAccounts(companyId: string | null, branchId: string | null): Promise<AccountRow[]>
    {
        let db = new NamirasoftERPMetaDatabase(this.props.account.token_manager, console.error);
        let filters: FilterItem[] = [];
        if (companyId)
            filters.push(new FilterItem(db.tables.account, db.tables.account.columns.company_id, false, FilterItemOperator.all.equals, companyId));
        if (branchId)
            filters.push(new FilterItem(db.tables.account, db.tables.account.columns.branch_id, false, FilterItemOperator.all.equals, branchId));
        let out: AccountRow[] = [];
        let page = 1;
        let size = 500;
        while (true)
        {
            let batch = await this.props.servers.erp.account.List(filters, page, size, [], null);
            out.push(...batch.rows);
            if (batch.rows.length < size) break;
            page += 1;
            if (page > 50) break;
        }
        return out;
    }

    private formatMoney(value: string | number): string
    {
        let n = Number(value) || 0;
        return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    private exportCsv = (): void =>
    {
        let { report } = this.state;
        if (!report) return;

        let lines: string[] = [];
        lines.push(["GIFI Code", "GIFI Name", "Statement Type", "Account Count", "Debit", "Credit", "Balance"].join(","));
        for (let r of report.rows)
            lines.push([
                this.csv(r.gifi),
                this.csv(r.gifi_name ?? ""),
                this.csv(r.statement_type ?? ""),
                String(r.account_count),
                this.csv(r.debit),
                this.csv(r.credit),
                this.csv(r.balance),
            ].join(","));

        lines.push("");
        lines.push("Detail:");
        lines.push(["GIFI Code", "Account Code", "Account Name", "Account Type", "Parent Account ID", "Debit", "Credit", "Balance"].join(","));
        for (let r of report.rows)
            for (let a of r.accounts)
                lines.push([
                    this.csv(r.gifi),
                    this.csv(a.account_code),
                    this.csv(a.account_name),
                    this.csv(a.account_type),
                    this.csv(a.parent_account_id ?? ""),
                    this.csv(a.debit),
                    this.csv(a.credit),
                    this.csv(a.balance),
                ].join(","));

        if (report.unmapped.length > 0)
        {
            lines.push("");
            lines.push("Unmapped Accounts:");
            lines.push(["Account Code", "Account Name", "Account Type", "Debit", "Credit", "Balance"].join(","));
            for (let a of report.unmapped)
                lines.push([
                    this.csv(a.account_code),
                    this.csv(a.account_name),
                    this.csv(a.account_type),
                    this.csv(a.debit),
                    this.csv(a.credit),
                    this.csv(a.balance),
                ].join(","));
        }

        lines.push("");
        lines.push("Totals:");
        lines.push(`Mapped Debit,${this.csv(report.totals.total_mapped_debit)}`);
        lines.push(`Mapped Credit,${this.csv(report.totals.total_mapped_credit)}`);
        lines.push(`Unmapped Debit,${this.csv(report.totals.total_unmapped_debit)}`);
        lines.push(`Unmapped Credit,${this.csv(report.totals.total_unmapped_credit)}`);
        lines.push(`Total Debit,${this.csv(report.totals.total_debit)}`);
        lines.push(`Total Credit,${this.csv(report.totals.total_credit)}`);
        lines.push(`Net Balance,${this.csv(report.totals.net_balance)}`);
        lines.push(`Difference,${this.csv(report.totals.difference)}`);
        lines.push(`Source Version,${this.csv(report.source_version ?? "")}`);

        let csv = lines.join("\n");
        let blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        let url = URL.createObjectURL(blob);
        let a = document.createElement("a");
        a.href = url;
        a.download = `gifi-report-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    private csv(v: string | number | null): string
    {
        let s = String(v ?? "");
        if (s.includes(",") || s.includes("\"") || s.includes("\n"))
            return `"${s.replace(/"/g, '""')}"`;
        return s;
    }

    private buildMappedColumns(): TableColumnInfo[]
    {
        let db = new NamirasoftERPMetaDatabase(this.props.account.token_manager, console.error);
        let gifi = db.tables.gifi;
        let account = db.tables.account;

        let gifiCol = new BaseMetaColumn(gifi, "gifi", "GIFI Code", gifi.columns.id.type, true);
        let gifiNameCol = new BaseMetaColumn(gifi, "gifi_name", "GIFI Name", gifi.columns.name.type, false);
        let statementCol = new BaseMetaColumn(gifi, "statement_type", "Statement", gifi.columns.statement_type.type, false);
        let accountCodeCol = new BaseMetaColumn(account, "account_code", "Account Code", account.columns.code.type, false);
        let accountNameCol = new BaseMetaColumn(account, "account_name", "Account Name", account.columns.name.type, false);
        let accountTypeCol = new BaseMetaColumn(account, "account_type", "Type", account.columns.type.type, false);
        let accountCountCol = new BaseMetaColumn(gifi, "account_count", "Accounts", gifi.columns.id.type, true);
        let debitCol = new BaseMetaColumn(gifi, "debit", "Debit", gifi.columns.id.type, true);
        let creditCol = new BaseMetaColumn(gifi, "credit", "Credit", gifi.columns.id.type, true);
        let balanceCol = new BaseMetaColumn(gifi, "balance", "Balance", gifi.columns.id.type, true);

        return [
            BaseColumnFormatter.getColumn(gifiCol, new StringFormatter(StringFormatterSizeType.Word)),
            BaseColumnFormatter.getColumn(gifiNameCol, new StringFormatter(StringFormatterSizeType.FullName)),
            BaseColumnFormatter.getColumn(statementCol, new StringFormatter(StringFormatterSizeType.Word)),
            BaseColumnFormatter.getColumn(accountCodeCol, new StringFormatter(StringFormatterSizeType.Word)),
            BaseColumnFormatter.getColumn(accountNameCol, new StringFormatter(StringFormatterSizeType.FullName)),
            BaseColumnFormatter.getColumn(accountTypeCol, new StringFormatter(StringFormatterSizeType.Word)),
            BaseColumnFormatter.getColumn(accountCountCol, new IntegerFormatter("90px")),
            BaseColumnFormatter.getColumn(debitCol, new FloatFormatter("150px")),
            BaseColumnFormatter.getColumn(creditCol, new FloatFormatter("150px")),
            BaseColumnFormatter.getColumn(balanceCol, new FloatFormatter("150px")),
        ];
    }

    private buildUnmappedColumns(): TableColumnInfo[]
    {
        let db = new NamirasoftERPMetaDatabase(this.props.account.token_manager, console.error);
        let account = db.tables.account;
        let debitCol = new BaseMetaColumn(account, "debit", "Debit", account.columns.code.type, true);
        let creditCol = new BaseMetaColumn(account, "credit", "Credit", account.columns.code.type, true);
        let balanceCol = new BaseMetaColumn(account, "balance", "Balance", account.columns.code.type, true);

        return [
            BaseColumnFormatter.getColumn(account.columns.code, new StringFormatter(StringFormatterSizeType.Word)),
            BaseColumnFormatter.getColumn(account.columns.name, new StringFormatter(StringFormatterSizeType.FullName)),
            BaseColumnFormatter.getColumn(account.columns.type, new StringFormatter(StringFormatterSizeType.Word)),
            BaseColumnFormatter.getColumn(debitCol, new FloatFormatter("150px")),
            BaseColumnFormatter.getColumn(creditCol, new FloatFormatter("150px")),
            BaseColumnFormatter.getColumn(balanceCol, new FloatFormatter("150px")),
        ];
    }

    private getUnmappedRows = async (): Promise<{ rows: UnmappedTableRow[]; count: number }> =>
    {
        let { report, accountsById } = this.state;
        if (!report) return { rows: [], count: 0 };

        type Totals = { debit: number; credit: number };
        let totalsById = new Map<string, Totals>();

        let include = new Set<string>();
        for (let u of report.unmapped)
        {
            include.add(u.account_id);
            totalsById.set(u.account_id, { debit: Number(u.debit), credit: Number(u.credit) });

            let p: string | null = u.parent_account_id;
            while (p)
            {
                let node = accountsById.get(p);
                if (!node) break;
                include.add(p);
                let t = totalsById.get(p);
                if (!t)
                {
                    t = { debit: 0, credit: 0 };
                    totalsById.set(p, t);
                }
                t.debit += Number(u.debit);
                t.credit += Number(u.credit);
                p = node.account_id ?? null;
            }
        }

        let rows: UnmappedTableRow[] = [];
        for (let id of include)
        {
            let acc = accountsById.get(id);
            let unmapped = report.unmapped.find(u => u.account_id === id);
            let t = totalsById.get(id) ?? { debit: 0, credit: 0 };
            let balance = t.debit - t.credit;
            rows.push({
                account_id: id,
                parent_account_id: (acc?.account_id ?? null) && include.has(acc!.account_id!) ? acc!.account_id! : null,
                code: acc?.code ?? unmapped?.account_code ?? "",
                name: acc?.name ?? unmapped?.account_name ?? "",
                type: (acc?.type ?? unmapped?.account_type ?? "") as string,
                debit: t.debit.toFixed(4),
                credit: t.credit.toFixed(4),
                balance: balance.toFixed(4),
                is_group: acc?.is_group ?? false,
            });
        }
        rows.sort((a, b) => a.code.localeCompare(b.code));
        return { rows, count: rows.length };
    };

    private getMappedRows = async (): Promise<{ rows: MappedTableRow[]; count: number }> =>
    {
        let { report } = this.state;
        if (!report) return { rows: [], count: 0 };

        let rows: MappedTableRow[] = [];
        for (let r of report.rows)
        {
            let parentId = GifiReportPage.PARENT_PREFIX + r.gifi;
            rows.push({
                line_id: parentId,
                parent_line_id: null,
                gifi: r.gifi,
                gifi_name: r.gifi_name,
                statement_type: r.statement_type,
                account_code: null,
                account_name: null,
                account_type: null,
                account_count: r.account_count,
                debit: r.debit,
                credit: r.credit,
                balance: r.balance,
            });
            for (let a of r.accounts)
                rows.push({
                    line_id: GifiReportPage.CHILD_PREFIX + a.account_id + "@" + r.gifi,
                    parent_line_id: parentId,
                    gifi: r.gifi,
                    gifi_name: null,
                    statement_type: null,
                    account_code: a.account_code,
                    account_name: a.account_name,
                    account_type: a.account_type,
                    account_count: null,
                    debit: a.debit,
                    credit: a.credit,
                    balance: a.balance,
                });
        }
        return { rows, count: rows.length };
    };

    private getMappedCellFormattedValue = (value: any, column: TableColumnInfo, row: TableRowInfo<MappedTableRow>, printable: boolean): any =>
    {
        let isParent = row.value.parent_line_id === null;

        if (column.name === "debit" || column.name === "credit" || column.name === "balance")
        {
            let formatted = this.formatMoney(value);
            let isZero = !value || Math.abs(Number(value)) < 0.01;
            if (isZero) return <span style={{ color: "#bdbdbd" }}>{formatted}</span>;
            if (column.name === "balance") return <strong>{formatted}</strong>;
            return formatted;
        }

        if (column.name === "gifi" || column.name === "gifi_name" || column.name === "statement_type" || column.name === "account_count")
        {
            if (!isParent) return "";
            if (value === null || value === undefined || value === "") return <span style={{ color: "#bdbdbd" }}>-</span>;
            return column.formatter.format(value, column, row, printable);
        }

        if (column.name === "account_code" || column.name === "account_name" || column.name === "account_type")
        {
            if (isParent) return "";
            if (value === null || value === undefined || value === "") return <span style={{ color: "#bdbdbd" }}>-</span>;
            if (column.name === "account_type") return renderTypeBadge(String(value));
            return column.formatter.format(value, column, row, printable);
        }

        return column.formatter.format(value, column, row, printable);
    };

    private getUnmappedCellFormattedValue = (value: any, column: TableColumnInfo, row: TableRowInfo<UnmappedTableRow>, printable: boolean): any =>
    {
        let isGroup = row.value.is_group;

        if (column.name === "debit" || column.name === "credit" || column.name === "balance")
        {
            let formatted = this.formatMoney(value);
            let isZero = !value || Math.abs(Number(value)) < 0.01;
            if (isZero) return <span style={{ color: "#bdbdbd" }}>{formatted}</span>;
            if (column.name === "balance") return <strong>{formatted}</strong>;
            return formatted;
        }
        if (column.name === "type")
        {
            if (isGroup) return <span style={{ color: "#888", fontStyle: "italic" }}>group</span>;
            if (value === null || value === undefined || value === "") return <span style={{ color: "#bdbdbd" }}>-</span>;
            return renderTypeBadge(String(value));
        }
        return column.formatter.format(value, column, row, printable);
    };

    private getUnmappedRowAttributes = (row_value: UnmappedTableRow, _row_index: number): { [key: string]: any } =>
    {
        if (row_value.is_group)
            return { style: { fontWeight: 600, color: "hsla(234, 64%, 22%, 1)", background: "#fafbff" } };
        return {};
    };

    private getMappedRowAttributes = (row_value: MappedTableRow, _row_index: number): { [key: string]: any } =>
    {
        let isParent = row_value.parent_line_id === null;
        if (isParent)
            return { style: { fontWeight: 600, color: "hsla(234, 64%, 22%, 1)", background: "#fafbff" } };
        return { style: { color: "#555", background: "#ffffff" } };
    };

    private renderResult(): React.ReactNode
    {
        let { report } = this.state;
        if (!report)
            return null;

        return (
            <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={CENTER_WRAP}>
                    <div style={CENTER_INNER}>
                        <NSLine />
                        {report.source_version && (
                            <div style={{ fontSize: 12, color: "#666", textAlign: "center" }}>
                                GIFI reference version: <strong>{report.source_version}</strong>
                            </div>
                        )}
                    </div>
                </div>

                <NSPanel title="Mapped to GIFI" style={FULL_PANEL}>
                    <div style={TABLE_SCROLL}>
                        <NSTable<MappedTableRow>
                            key={"mapped:" + String(this.state.scopeVersion) + ":" + String(report.rows.length)}
                            name="gifi-mapped"
                            checkbox={false}
                            columns={this.buildMappedColumns()}
                            getRows={this.getMappedRows}
                            getRowKey={(row) => row.value.line_id}
                            getCellFormattedValue={this.getMappedCellFormattedValue}
                            getRowAttributes={this.getMappedRowAttributes}
                            sort={{ enabled: false }}
                            hierarchy={{ id: "line_id", parent: "parent_line_id" }}
                            ui={{ pagination: false, grow: true }}
                        />
                    </div>
                </NSPanel>

                <NSPanel title={`Unmapped Accounts (${report.unmapped.length})`} style={FULL_PANEL}>
                    {report.unmapped.length === 0 && (
                        <div style={{ padding: 12, color: "#888", textAlign: "center" }}>All active accounts are mapped to a GIFI code.</div>
                    )}
                    {report.unmapped.length > 0 && (
                        <div style={TABLE_SCROLL}>
                            <NSTable<UnmappedTableRow>
                                key={"unmapped:" + String(this.state.scopeVersion) + ":" + String(report.unmapped.length)}
                                name="gifi-unmapped"
                                checkbox={false}
                                columns={this.buildUnmappedColumns()}
                                getRows={this.getUnmappedRows}
                                getRowKey={(row) => row.value.account_id}
                                getCellFormattedValue={this.getUnmappedCellFormattedValue}
                                getRowAttributes={this.getUnmappedRowAttributes}
                                sort={{ enabled: false }}
                                hierarchy={{ id: "account_id", parent: "parent_account_id" }}
                                ui={{ pagination: false, grow: true }}
                            />
                        </div>
                    )}
                </NSPanel>

                <div style={CENTER_WRAP}>
                    <div style={CENTER_INNER}>
                        <NSPanel title="Control Totals" style={FULL_PANEL}>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <tbody>
                                    <tr><td style={totalLabelStyle}>Mapped (debit / credit)</td><td style={totalValueStyle}>{this.formatMoney(report.totals.total_mapped_debit)} / {this.formatMoney(report.totals.total_mapped_credit)}</td></tr>
                                    <tr><td style={totalLabelStyle}>Unmapped (debit / credit)</td><td style={totalValueStyle}>{this.formatMoney(report.totals.total_unmapped_debit)} / {this.formatMoney(report.totals.total_unmapped_credit)}</td></tr>
                                    <tr><td style={totalLabelStyle}>Total (debit / credit)</td><td style={{ ...totalValueStyle, fontWeight: "bold" }}>{this.formatMoney(report.totals.total_debit)} / {this.formatMoney(report.totals.total_credit)}</td></tr>
                                    <tr><td style={totalLabelStyle}>Net balance (debit − credit)</td><td style={{ ...totalValueStyle, fontWeight: "bold", color: Math.abs(Number(report.totals.net_balance)) < 0.01 ? "#047857" : "#c53030" }}>{this.formatMoney(report.totals.net_balance)}</td></tr>
                                </tbody>
                            </table>
                        </NSPanel>
                    </div>
                </div>
            </div>
        );
    }

    override render(): React.ReactNode
    {
        return (
            <ErpLayout
                {...this.props}
                mode={ErpScopeMode.Filter}
                fields={[ErpScopeField.Company, ErpScopeField.Branch, ErpScopeField.FiscalYear]}
                action={{ title: "GIFI Financial Information" }}
            >
                <div style={{ width: "100%" }}>
                    <div style={CENTER_WRAP}>
                        <div style={CENTER_INNER}>
                            <NSRow style={{ flexWrap: "wrap", alignItems: "center", gap: 8, justifyContent: "center" }}>
                                <NSBoxDate ref={this.FromDate} title="From Date" required={false} />
                                <NSBoxDate ref={this.AsOf} required title="As Of" defaultValue={new Date()} />
                                <NSBoxEnum<GifiStatementType>
                                    ref={this.Statement}
                                    title="Statement"
                                    required={false}
                                    multiple={false}
                                    getEnumObject={() => GifiStatementType}
                                />
                                <NSPanel title="Options" style={{ padding: 8, display: "flex", flexDirection: "column", gap: 4, width: "auto" }}>
                                    <NSRow style={{ gap: 8, flexWrap: "wrap" }}>
                                        <NSBoxBoolean ref={this.ShowZero} required={false} checked={false} title="Show Zero" hideHeader style={{ width: 150, flexGrow: 0 }} />
                                        <NSBoxBoolean ref={this.ConvertToBranchCurrency} required={false} checked={false} title="Convert To Branch Currency" hideHeader style={{ width: 220, flexGrow: 0 }} />
                                    </NSRow>
                                </NSPanel>
                                <NSButtonBlue title="Run Report" onClick={{ action: this.runReport, showLoading: true }} />
                                {this.state.report && (
                                    <NSButtonBlue title="Export CSV" onClick={{ action: (done) => { this.exportCsv(); done(); }, showLoading: false }} />
                                )}
                            </NSRow>
                            <NSSpace size={NSSpaceSizeType.MINI} />
                        </div>
                    </div>
                    {this.state.loading && <NSLoading />}
                    {!this.state.loading && this.renderResult()}
                </div>
            </ErpLayout>
        );
    }
}

const totalLabelStyle: React.CSSProperties = { padding: "8px 12px", borderBottom: "1px solid #eee", fontSize: 13 };
const totalValueStyle: React.CSSProperties = { padding: "8px 12px", borderBottom: "1px solid #eee", fontSize: 13, textAlign: "right" };